import { ref } from 'vue';
import { defineStore } from 'pinia';
import { useMatterStore } from './matterStore';
import { captureEvent } from '../analytics';

/**
 * Findings for a matter — the free-text conclusions that narratives are
 * assembled from. Backed by the findings API (routers/findings_router.py):
 *   POST /api/Matter/{id}/Finding   — record a finding (the chat "Add to
 *                                     findings" action)
 *   GET  /api/Matter/{id}/Findings  — list, newest first (the FindingsPage)
 * This store does both: create (the seam the chat action writes through) and
 * list (read-only, for the findings workspace).
 */

// Mirror of FindingResponse (services/findings/response_schemas.py).
export interface Finding {
    id: string;
    description: string;
    created_by_user_id: string;
    // FK to the matter_data_subsets registry row provisioned for this finding.
    matter_data_subset_id: string;
    // FK to the narrative this finding is filed under, or null when unfiled.
    narrative_id: string | null;
    created_on: string;
}

export const useFindingsStore = defineStore('findings', () => {
    // --- Listing (FindingsPage) ---------------------------------------------
    const findings = ref<Finding[]>([]);
    // The matter the current `findings` belong to, so we can skip a refetch
    // when the page remounts for the same matter and reset on a matter change.
    const loadedMatterId = ref<string | null>(null);
    const loading = ref(false);
    const error = ref<string | null>(null);

    async function fetchFindings(matterId: string): Promise<void> {
        loading.value = true;
        error.value = null;
        try {
            const response = await fetch(`/api/Matter/${matterId}/Findings`);
            if (!response.ok) {
                const body = await response.json().catch(() => ({}));
                throw new Error(body?.detail ?? `Failed to load findings (${response.status})`);
            }
            // Guard against a matter switch mid-flight: a slower earlier request
            // must not overwrite a newer matter's list.
            const data = (await response.json()) as Finding[];
            findings.value = data;
            loadedMatterId.value = matterId;
        } catch (e: unknown) {
            error.value = e instanceof Error ? e.message : 'Unknown error';
            findings.value = [];
        } finally {
            loading.value = false;
        }
    }

    function reset() {
        findings.value = [];
        loadedMatterId.value = null;
        loading.value = false;
        error.value = null;
    }

    // --- Creation (chat "Add to findings" action) ---------------------------
    // True while a create request is in flight, so the dialog can disable its
    // submit button and show a busy state without double-posting.
    const creating = ref(false);

    // Record a new finding in the current matter via POST /Finding. The body
    // carries the free-text description plus the source `chatId` (the agent
    // response being saved); the backend derives the finding's evidence subset
    // server-side from that chat's persisted query results (the client never
    // sends the reference list). Author + timestamp are set server-side.
    // `narrativeId` optionally files the finding under an existing narrative.
    // `evidenceCount` is analytics-only. Resolves to the created Finding, or
    // null on failure / no current matter (the caller surfaces the error).
    async function createFinding(
        description: string,
        opts: { chatId?: string; narrativeId?: string | null; evidenceCount?: number } = {},
    ): Promise<Finding | null> {
        const matterStore = useMatterStore();
        const matterId = matterStore.currentMatter?.RecordId;
        if (!matterId || !description.trim() || creating.value) return null;

        creating.value = true;
        try {
            const response = await fetch(`/api/Matter/${matterId}/Finding`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    description,
                    chat_id: opts.chatId ?? null,
                    narrative_id: opts.narrativeId ?? null,
                }),
            });
            if (!response.ok) return null;
            const finding = (await response.json()) as Finding;

            // Keep the FindingsPage list fresh without forcing a refetch: when
            // the store already holds this matter's findings, prepend the new
            // one (the API lists newest-first). Without this the page's
            // load-once cache (loadedMatterId) shows a stale list — the new
            // finding only appears after a hard refresh.
            if (loadedMatterId.value === matterId) {
                findings.value = [finding, ...findings.value];
            }

            // Counts/ids/booleans only — never the description free-text (may
            // contain anything). evidence_count is 0 when the response's
            // reference count isn't known yet at save time. linked_to_narrative
            // reflects whether the persisted finding was filed under a narrative
            // (segments filed vs. unfiled findings without exposing which one).
            captureEvent('finding_created', {
                matter_id: matterId,
                evidence_count: opts.evidenceCount ?? 0,
                linked_to_narrative: finding.narrative_id != null,
            });
            return finding;
        } catch (err) {
            console.error('Failed to create finding:', err);
            return null;
        } finally {
            creating.value = false;
        }
    }

    return {
        findings,
        loadedMatterId,
        loading,
        error,
        fetchFindings,
        reset,
        creating,
        createFinding,
    };
});
